// ArtistsPage 리팩토링: React Table + 검색 + 페이지네이션 + 공용 컴포넌트 적용
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import ButtonItem from '../components/ButtonItem';
import ArtistsTable from '../components/ArtistsTable';
import SearchInput from '../components/common/SearchInput';
import Pagination from '../components/common/Pagination';
import SectionTabs from '../components/SectionTabs';
import { Building2, Mic2, Disc3, Music, Package } from 'lucide-react';

function ArtistPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialPage = parseInt(params.get('page')) || 1;
  const initialSearch = params.get('search') || '';

  const [loading, setLoading] = useState(false);
  const [artists, setArtists] = useState([]);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [searchKeyword, setSearchKeyword] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);


  const fetchArtists = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const response = await axios.get(`/artist`, {
          params: {
            page,
            limit: 10,
            search: searchKeyword,
          },
        });
        setArtists(response.data.items);
        setTotalPages(response.data.totalPages);
        setCurrentPage(response.data.currentPage);
        setTotalCount(response.data.totalCount);
      } catch (error) {
        console.error('서버 접속 에러:', error);
      } finally {
        setLoading(false);
      }
    },
    [searchKeyword]
  );

  useEffect(() => {
    fetchArtists(currentPage);
  }, [currentPage, fetchArtists]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    const searchParam = searchKeyword ? `&search=${encodeURIComponent(searchKeyword)}` : '';
    navigate(`/Artists?page=${newPage}${searchParam}`);
  };

  const handleDownload = async () => {
    try {
      // 서버에서 전체 데이터 조회
      const params = { search: searchTerm, page: 1, limit: totalCount };

      const response = await axios.get(`/artist`, { params });
      const allClients = response.data.items;
      // UI 필터와 동일하게 월·검색어 적용
      const exportClients = allClients.filter((c) =>
        c.nameKr.includes(searchTerm)
      );

      const exportData = exportClients.map((c, idx) => {
        return {
          ID: c.artistId,
          아티스트명: c.nameKr,
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '아티스트');
      XLSX.writeFile(wb, `아티스트.xlsx`);
    } catch (error) {
      console.error('엑셀 다운로드 중 에러:', error);
    }
  };

  const dataTabs = [
    { label: '권리사', to: '/Clients', icon: Building2 },
    { label: '아티스트', to: '/Artists', icon: Mic2 },
    { label: '앨범', to: '/Albums', icon: Disc3 },
    { label: '곡', to: '/Songs', icon: Music },
    { label: '음반', to: '/PhysicalAlbums', icon: Package },
  ];

  return (
    <>
        <div className="flex mb-8 justify-center items-center">
          <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            <span>아티스트 리스트</span>
          </div>
        </div>
        <SectionTabs items={dataTabs} />

        <SearchInput
          placeholder="아티스트명 또는 ID로 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={() => {
            setCurrentPage(1);
            setSearchKeyword(searchTerm);
            const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
            navigate(`/Artists?page=1${searchParam}`);
          }}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
            <div className="text-sm text-slate-400">데이터 불러오는 중...</div>
          </div>
        ) : (
          <>
            <ArtistsTable
              data={artists}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={10}
              searchKeyword={searchKeyword}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />

            <div className="w-full flex justify-end items-center mt-6">
              <ButtonItem
                text="등록"
                color="blue"
                onClick={() => navigate('/ArtistWrite')}
              />
              <button
                onClick={handleDownload}
                className="flex items-center px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-sm font-semibold transition-colors ml-2">
                <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                다운로드
              </button>
            </div>
          </>
        )}
    </>
  );
}

export default ArtistPage;
